import { Building2, Coffee, ShoppingBag } from "lucide-react";
import { useState } from "react";

interface BusinessType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}

const businessTypes: BusinessType[] = [
  {
    id: "property",
    name: "Property Management",
    description: "Commercial offices, retail spaces, mixed-use developments",
    icon: <Building2 className="w-10 h-10 sm:w-12 sm:h-12" />,
  },
  {
    id: "hospitality",
    name: "Hospitality",
    description: "Hotels, restaurants, bars, entertainment venues",
    icon: <Coffee className="w-10 h-10 sm:w-12 sm:h-12" />,
  },
  {
    id: "retail",
    name: "Retail",
    description: "Shopping centres, flagship stores, pop-up spaces",
    icon: <ShoppingBag className="w-10 h-10 sm:w-12 sm:h-12" />,
  },
];

interface Props {
  onBusinessSelect: (businessType: string) => void;
}

export default function LandingSpaceType({ onBusinessSelect }: Props) {
  const [selectedBusiness, setSelectedBusiness] = useState<string | null>(null);

  const handleSelect = (businessId: string) => {
    setSelectedBusiness(businessId);
    setTimeout(() => onBusinessSelect(businessId), 300);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center p-3 sm:p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-24 h-24 sm:w-32 sm:h-32 bg-blue-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-32 h-32 sm:w-40 sm:h-40 bg-purple-500 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-20 h-20 sm:w-24 sm:h-24 bg-cyan-500 rounded-full filter blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="max-w-4xl w-full relative z-10">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          {/* Breadcrumb/Status indicator - Mobile optimized */}
          <div className="inline-flex items-center bg-blue-500/20 backdrop-blur-sm rounded-full px-3 py-2 sm:px-4 mb-6 sm:mb-8 border border-blue-500/30">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse mr-2 sm:mr-3"></div>
            <span className="text-blue-400 text-xs sm:text-sm font-medium">
              CULTURE LAB • STEP 1 OF 4
            </span>
          </div>

          {/* Mobile-first heading sizing */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent leading-tight">
            Welcome to Culture Lab
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed px-2">
            Transform your commercial space into a cultural experience. Tell us
            about your business to unlock personalised insights.
          </p>
        </div>

        {/* Mobile-optimized grid - single column on very small screens */}
        <div className="grid gap-4 sm:gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {businessTypes.map((business, index) => (
            <button
              key={business.id}
              onClick={() => handleSelect(business.id)}
              className={`group relative p-4 sm:p-6 md:p-8 rounded-2xl border-2 transition-all duration-500 text-left hover:shadow-2xl hover:shadow-blue-500/10 min-h-[160px] sm:min-h-[180px] ${
                selectedBusiness === business.id
                  ? "border-blue-500 bg-slate-800/60 backdrop-blur-sm shadow-2xl shadow-blue-500/20 scale-105"
                  : "border-slate-600/50 bg-slate-800/40 backdrop-blur-sm hover:border-blue-400/60 hover:bg-slate-800/60 hover:scale-102"
              }`}
              style={{
                animationDelay: `${index * 100}ms`,
              }}
            >
              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="relative z-10 h-full flex flex-col">
                {/* Icon with gradient background - Mobile responsive */}
                <div
                  className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center mb-4 sm:mb-6 transition-all duration-300 flex-shrink-0 ${
                    selectedBusiness === business.id
                      ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
                      : "bg-gradient-to-r from-slate-700 to-slate-600 text-slate-300 group-hover:from-blue-500/80 group-hover:to-cyan-500/80 group-hover:text-white"
                  }`}
                >
                  {business.icon}
                </div>

                {/* Content - Flexible for different screen sizes */}
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-white group-hover:text-blue-100 transition-colors leading-tight">
                      {business.name}
                    </h3>
                    <p className="text-slate-400 text-sm sm:text-base leading-relaxed group-hover:text-slate-300 transition-colors">
                      {business.description}
                    </p>
                  </div>
                </div>

                {/* Selection indicator - Mobile positioned */}
                {selectedBusiness === business.id && (
                  <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-center">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                  </div>
                )}
              </div>

              {/* Animated border effect */}
              <div
                className={`absolute inset-0 rounded-2xl transition-opacity duration-300 ${
                  selectedBusiness === business.id ? "opacity-100" : "opacity-0"
                }`}
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 opacity-20 animate-pulse"></div>
              </div>
            </button>
          ))}
        </div>

        {/* Bottom hint - Mobile optimized */}
        <div className="text-center mt-8 sm:mt-12 px-4">
          <p className="text-slate-400 text-sm">
            Select your business type to continue your cultural transformation
            journey
          </p>
        </div>
      </div>
    </div>
  );
}
